import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Clock, FileSpreadsheet, GitCommitHorizontal, Minus, Plus, RefreshCw, User, X } from 'lucide-react';
import { useState } from 'react';
import { useUserLookup } from '../../../../hooks/useUserLookup';
import { projectDataClient } from '../../../../lib/connect/clients';
/**
 * WorkbookVersionHistory — drawer showing committed workbook versions
 * with cell-level diffs between any two versions.
 */ export function WorkbookVersionHistory({ isOpen, onClose, projectId, volumeId }) {
    const userLookup = useUserLookup(projectId);
    const [diffPair, setDiffPair] = useState(null);
    const { data: versions, isLoading } = useQuery({
        queryKey: [
            'workbookVersions',
            projectId,
            volumeId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listWorkbookVersions({
                projectId,
                volumeId,
                limit: 50
            });
            return resp.versions ?? [];
        },
        enabled: isOpen && !!volumeId
    });
    // Diff query — only fires when diffPair is set.
    const { data: diffData, isLoading: diffLoading } = useQuery({
        queryKey: [
            'workbookDiff',
            projectId,
            volumeId,
            diffPair?.from,
            diffPair?.to
        ],
        queryFn: async ()=>{
            if (!diffPair) return null;
            return projectDataClient.diffWorkbookVersions({
                projectId,
                volumeId,
                fromVersion: diffPair.from,
                toVersion: diffPair.to
            });
        },
        enabled: !!diffPair
    });
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]",
                onClick: onClose,
                role: "presentation"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-slate-200 bg-white shadow-xl sm:w-[480px]",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3 border-b border-slate-200 px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsx(FileSpreadsheet, {
                                className: "h-4 w-4 text-slate-500"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "min-w-0 flex-1",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "text-sm font-semibold text-slate-900",
                                    children: "Workbook Versions"
                                })
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: onClose,
                                className: "rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600",
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-y-auto",
                        children: isLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center py-16",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500"
                            })
                        }) : !versions || versions.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center justify-center py-16 text-center",
                            children: [
                                /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                    className: "h-6 w-6 text-slate-300"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mt-2 text-sm text-slate-500",
                                    children: "No versions yet"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-1 max-w-[240px] text-xs text-slate-400",
                                    children: [
                                        "Click ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-semibold text-emerald-700",
                                            children: "Commit"
                                        }),
                                        " in the editor to create a snapshot."
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "p-4 space-y-3",
                            children: versions.map((v, i)=>{
                                const userName = v.createdBy ? userLookup.get(v.createdBy) || v.createdBy.slice(0, 8) : 'System';
                                const isLatest = i === 0;
                                const prevVersion = versions[i + 1]?.version ?? 0;
                                const isDiffOpen = diffPair?.from === prevVersion && diffPair?.to === v.version;
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-lg border border-slate-100 bg-slate-50/50 p-3",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: `text-[12px] font-semibold ${isLatest ? 'text-emerald-700' : 'text-slate-700'}`,
                                                    children: [
                                                        "v",
                                                        v.version,
                                                        isLatest && /*#__PURE__*/ _jsx("span", {
                                                            className: "ml-1.5 rounded-full bg-emerald-100 px-1.5 py-px text-[9px] font-bold text-emerald-700",
                                                            children: "LATEST"
                                                        })
                                                    ]
                                                }),
                                                v.actorType === 'ai' && /*#__PURE__*/ _jsx("span", {
                                                    className: "rounded border border-violet-200 bg-violet-50 px-1.5 py-px text-[10px] font-medium text-violet-700",
                                                    children: "AI"
                                                })
                                            ]
                                        }),
                                        v.message && /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1 text-[12px] text-slate-800",
                                            children: v.message
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "flex items-center gap-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(User, {
                                                            className: "h-3 w-3"
                                                        }),
                                                        " ",
                                                        userName
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "flex items-center gap-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Clock, {
                                                            className: "h-3 w-3"
                                                        }),
                                                        v.createdAt ? formatRelative(v.createdAt.toDate()) : ''
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: formatBytes(Number(v.sizeBytes))
                                                }),
                                                v.sheetNames.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        v.sheetNames.length,
                                                        " sheet",
                                                        v.sheetNames.length === 1 ? '' : 's'
                                                    ]
                                                })
                                            ]
                                        }),
                                        v.diffSummary && /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1.5 text-[11px] text-slate-600 italic",
                                            children: v.diffSummary
                                        }),
                                        i < versions.length - 1 && /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>setDiffPair(isDiffOpen ? null : {
                                                    from: prevVersion,
                                                    to: v.version
                                                }),
                                            className: "mt-2 text-[10px] font-medium text-blue-600 hover:text-blue-800",
                                            children: isDiffOpen ? 'Hide changes' : `Show changes from v${prevVersion}`
                                        }),
                                        isDiffOpen && /*#__PURE__*/ _jsx("div", {
                                            className: "mt-2 rounded border border-slate-200 bg-white",
                                            children: diffLoading ? /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-center py-4",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "ml-2 text-[11px] text-slate-400",
                                                        children: "Computing diff..."
                                                    })
                                                ]
                                            }) : diffData ? /*#__PURE__*/ _jsx(CellDiffView, {
                                                diff: diffData
                                            }) : null
                                        })
                                    ]
                                }, v.id);
                            })
                        })
                    })
                ]
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Cell Diff View — renders cell-level changes per sheet
// ---------------------------------------------------------------------------
function CellDiffView({ diff }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "p-2 space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-[11px]",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "font-semibold text-slate-700",
                        children: [
                            diff.totalChanges,
                            " change",
                            diff.totalChanges === 1 ? '' : 's'
                        ]
                    }),
                    diff.addedSheets.length > 0 && /*#__PURE__*/ _jsxs("span", {
                        className: "text-emerald-600",
                        children: [
                            "+",
                            diff.addedSheets.length,
                            " sheet",
                            diff.addedSheets.length === 1 ? '' : 's'
                        ]
                    }),
                    diff.removedSheets.length > 0 && /*#__PURE__*/ _jsxs("span", {
                        className: "text-rose-600",
                        children: [
                            "-",
                            diff.removedSheets.length,
                            " sheet",
                            diff.removedSheets.length === 1 ? '' : 's'
                        ]
                    })
                ]
            }),
            diff.sheets.map((sheet)=>/*#__PURE__*/ _jsxs("div", {
                    className: "rounded border border-slate-100 overflow-hidden",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2 bg-slate-50 px-2 py-1.5 text-[11px]",
                            children: [
                                /*#__PURE__*/ _jsx(FileSpreadsheet, {
                                    className: "h-3 w-3 text-slate-400"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-slate-700",
                                    children: sheet.sheetName
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "ml-auto flex items-center gap-2 text-[10px]",
                                    children: [
                                        sheet.cellsAdded > 0 && /*#__PURE__*/ _jsxs("span", {
                                            className: "flex items-center gap-0.5 text-emerald-600",
                                            children: [
                                                /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-2.5 w-2.5"
                                                }),
                                                sheet.cellsAdded
                                            ]
                                        }),
                                        sheet.cellsModified > 0 && /*#__PURE__*/ _jsxs("span", {
                                            className: "flex items-center gap-0.5 text-amber-600",
                                            children: [
                                                /*#__PURE__*/ _jsx(RefreshCw, {
                                                    className: "h-2.5 w-2.5"
                                                }),
                                                sheet.cellsModified
                                            ]
                                        }),
                                        sheet.cellsRemoved > 0 && /*#__PURE__*/ _jsxs("span", {
                                            className: "flex items-center gap-0.5 text-rose-600",
                                            children: [
                                                /*#__PURE__*/ _jsx(Minus, {
                                                    className: "h-2.5 w-2.5"
                                                }),
                                                sheet.cellsRemoved
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        sheet.changes.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "max-h-64 overflow-y-auto",
                            children: [
                                /*#__PURE__*/ _jsxs("table", {
                                    className: "w-full text-[11px]",
                                    children: [
                                        /*#__PURE__*/ _jsx("thead", {
                                            className: "bg-slate-50 sticky top-0",
                                            children: /*#__PURE__*/ _jsxs("tr", {
                                                children: [
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-2 py-1 text-left font-medium text-slate-500 w-16",
                                                        children: "Cell"
                                                    }),
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-2 py-1 text-left font-medium text-slate-500",
                                                        children: "Before"
                                                    }),
                                                    /*#__PURE__*/ _jsx("th", {
                                                        className: "px-2 py-1 text-left font-medium text-slate-500",
                                                        children: "After"
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("tbody", {
                                            children: sheet.changes.slice(0, 100).map((c, i)=>/*#__PURE__*/ _jsxs("tr", {
                                                    className: "border-t border-slate-50",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("td", {
                                                            className: "px-2 py-1 font-mono text-slate-600",
                                                            children: [
                                                                colIndexToLetter(c.col),
                                                                c.row + 1
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: `px-2 py-1 ${c.changeType === 'removed' || c.changeType === 'modified' ? 'bg-rose-50 text-rose-800' : 'text-slate-400'}`,
                                                            children: c.oldValue || '—'
                                                        }),
                                                        /*#__PURE__*/ _jsx("td", {
                                                            className: `px-2 py-1 ${c.changeType === 'added' || c.changeType === 'modified' ? 'bg-emerald-50 text-emerald-800' : 'text-slate-400'}`,
                                                            children: c.newValue || '—'
                                                        })
                                                    ]
                                                }, i))
                                        })
                                    ]
                                }),
                                sheet.changes.length > 100 && /*#__PURE__*/ _jsxs("div", {
                                    className: "px-2 py-1.5 text-center text-[10px] text-slate-400 bg-slate-50",
                                    children: [
                                        "Showing first 100 of ",
                                        sheet.changes.length,
                                        " changes"
                                    ]
                                })
                            ]
                        })
                    ]
                }, sheet.sheetName)),
            diff.totalChanges === 0 && diff.addedSheets.length === 0 && diff.removedSheets.length === 0 && /*#__PURE__*/ _jsx("div", {
                className: "text-center text-[11px] text-slate-400 italic py-2",
                children: "No changes detected"
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function colIndexToLetter(col) {
    let result = '';
    let c = col;
    while(c >= 0){
        result = String.fromCharCode(c % 26 + 65) + result;
        c = Math.floor(c / 26) - 1;
    }
    return result;
}
function formatRelative(date) {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}
function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
